import React, { useState, useEffect } from 'react';
import './EmailPreviewEditModal.css';

const EmailPreviewEditModal = ({
  isOpen,
  onClose,
  onSend,
  templateKey,
  variables = {},
  recipientEmail,
  ccEmails = [],
  title = 'Email Preview',
  loading = false,
  showEditToggle = true,
  allowTemplateUpdate = false
}) => {
  const [editMode, setEditMode] = useState(false);
  const [subject, setSubject] = useState('');
  const [htmlContent, setHtmlContent] = useState('');
  const [originalTemplate, setOriginalTemplate] = useState(null);
  const [isLoadingTemplate, setIsLoadingTemplate] = useState(false);
  const [error, setError] = useState(null);

  // Fetch and preview template when modal opens
  useEffect(() => {
    if (isOpen && templateKey) {
      fetchTemplatePreview();
    }
  }, [isOpen, templateKey, variables]);

  const fetchTemplatePreview = async () => {
    try {
      setIsLoadingTemplate(true);
      setError(null);

      const response = await fetch(`${process.env.REACT_APP_BACKEND_API_URL}/email-templates/preview`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          templateKey,
          variables
        })
      });

      if (!response.ok) {
        throw new Error('Failed to fetch email template');
      }

      const data = await response.json();
      setSubject(data.subject);
      setHtmlContent(data.htmlContent);
      setOriginalTemplate(data.originalTemplate);
    } catch (error) {
      console.error('Error fetching template preview:', error);
      setError('Failed to load email template. Please try again.');
    } finally {
      setIsLoadingTemplate(false);
    }
  };

  const handleSend = () => {
    // Pass the edited subject and content to the parent
    onSend({
      subject,
      htmlContent,
      recipientEmail,
      ccEmails
    });
  };

  const handleReset = () => {
    // Reset to original template
    fetchTemplatePreview();
    setEditMode(false);
  };

  const handleUpdateTemplate = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_API_URL}/email-templates/update`, {
        method: 'PUT',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          templateKey,
          subject,
          htmlContent
        })
      });

      if (!response.ok) {
        throw new Error('Failed to update template');
      }

      alert('Template updated successfully!');
      // Refresh the template to show the updated version
      await fetchTemplatePreview();
      setEditMode(false);
    } catch (error) {
      console.error('Error updating template:', error);
      alert('Failed to update template. Please try again.');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="email-modal-overlay" onClick={onClose}>
      <div className="email-modal-container" onClick={(e) => e.stopPropagation()}>
        <div className="email-modal-header">
          <h2>{title}</h2>
          <button className="email-modal-close-btn" onClick={onClose}>×</button>
        </div>

        <div className="email-modal-body">
          {isLoadingTemplate ? (
            <div className="email-modal-loading">
              <div className="spinner"></div>
              <p>Loading email template...</p>
            </div>
          ) : error ? (
            <div className="email-modal-error">
              <p>{error}</p>
              <button onClick={fetchTemplatePreview} className="retry-btn">Retry</button>
            </div>
          ) : (
            <>
              {/* Email Recipients Info */}
              <div className="email-recipients-info">
                <div className="recipient-row">
                  <strong>To:</strong>
                  <span>{recipientEmail}</span>
                </div>
                {ccEmails && ccEmails.length > 0 && (
                  <div className="recipient-row">
                    <strong>CC:</strong>
                    <span>{ccEmails.join(', ')}</span>
                  </div>
                )}
              </div>

              {/* Edit Mode Toggle */}
              {showEditToggle && (
                <div className="email-edit-toggle">
                  <label className="toggle-switch">
                    <input
                      type="checkbox"
                      checked={editMode}
                      onChange={(e) => setEditMode(e.target.checked)}
                    />
                    <span className="toggle-slider"></span>
                  </label>
                  <span>Edit email before sending</span>
                </div>
              )}

              {editMode ? (
                /* Edit Mode */
                <div className="email-edit-mode">
                  <div className="form-group">
                    <label htmlFor="email-subject">Subject:</label>
                    <input
                      id="email-subject"
                      type="text"
                      value={subject}
                      onChange={(e) => setSubject(e.target.value)}
                      className="email-subject-input"
                      placeholder="Email subject..."
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="email-content">Email Content (HTML):</label>
                    <textarea
                      id="email-content"
                      value={htmlContent}
                      onChange={(e) => setHtmlContent(e.target.value)}
                      className="email-content-textarea"
                      rows="15"
                      placeholder="Email HTML content..."
                    />
                  </div>

                  <div className="flex gap-3 justify-end mt-4">
                    <button
                      onClick={handleReset}
                      className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors duration-200 font-medium"
                    >
                      Reset to Original
                    </button>
                    {allowTemplateUpdate && (
                      <button
                        onClick={handleUpdateTemplate}
                        className="px-4 py-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg hover:from-blue-700 hover:to-indigo-700 transition-all duration-200 font-medium shadow-md hover:shadow-lg"
                      >
                        Update Template
                      </button>
                    )}
                  </div>
                </div>
              ) : (
                /* Preview Mode */
                <div className="email-preview-mode">
                  <div className="email-subject-preview">
                    <strong>Subject:</strong> {subject}
                  </div>

                  <div className="email-content-preview">
                    <div className="email-preview-label">Preview:</div>
                    <div
                      className="email-html-preview"
                      dangerouslySetInnerHTML={{ __html: htmlContent }}
                    />
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        <div className="email-modal-footer">
          <button
            className="btn-secondary"
            onClick={onClose}
            disabled={loading}
          >
            Cancel
          </button>
          <button
            className="btn-primary"
            onClick={handleSend}
            disabled={loading || isLoadingTemplate || error}
          >
            {loading ? 'Sending...' : 'Send Email'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EmailPreviewEditModal;
